import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, MailCheck, RefreshCw, Settings2 } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import type { MailAccount, MailConnectionInput } from "../types";

export const MAIL_ACCOUNT_QUERY_KEY = ["mail-account"] as const;

export const useMailAccount = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: MAIL_ACCOUNT_QUERY_KEY,
    queryFn: () => dataProvider.mailAccountGet(),
    staleTime: 30_000,
  });
};

type ConnectionForm = MailConnectionInput;

const emptyForm: ConnectionForm = {
  email: "",
  displayName: "",
  imapHost: "",
  imapPort: 993,
  imapSecure: true,
  imapUsername: "",
  imapPassword: "",
  smtpHost: "",
  smtpPort: 465,
  smtpSecure: true,
  smtpUsername: "",
  smtpPassword: "",
};

const getPreset = (email: string) => {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return null;
  if (["gmail.com", "googlemail.com"].includes(domain)) {
    return {
      imapHost: "imap.gmail.com",
      smtpHost: "smtp.gmail.com",
      smtpPort: 465,
      smtpSecure: true,
    };
  }
  if (["yandex.ru", "ya.ru", "yandex.com"].includes(domain)) {
    return {
      imapHost: "imap.yandex.ru",
      smtpHost: "smtp.yandex.ru",
      smtpPort: 465,
      smtpSecure: true,
    };
  }
  if (["mail.ru", "inbox.ru", "bk.ru", "list.ru"].includes(domain)) {
    return {
      imapHost: "imap.mail.ru",
      smtpHost: "smtp.mail.ru",
      smtpPort: 465,
      smtpSecure: true,
    };
  }
  if (["outlook.com", "hotmail.com", "live.com"].includes(domain)) {
    return {
      imapHost: "outlook.office365.com",
      smtpHost: "smtp.office365.com",
      smtpPort: 587,
      smtpSecure: false,
    };
  }
  return null;
};

const accountToForm = (account?: MailAccount | null): ConnectionForm => ({
  ...emptyForm,
  email: account?.email || "",
  displayName: account?.display_name || "",
  imapHost: account?.imap_host || "",
  imapPort: account?.imap_port || 993,
  imapSecure: account?.imap_secure ?? true,
  imapUsername: account?.email || "",
  smtpHost: account?.smtp_host || "",
  smtpPort: account?.smtp_port || 465,
  smtpSecure: account?.smtp_secure ?? true,
  smtpUsername: account?.email || "",
});

export const MailAccountSettings = ({
  compact = false,
}: {
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: account, isPending } = useMailAccount();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ConnectionForm>(emptyForm);

  useEffect(() => {
    if (open) setForm(accountToForm(account));
  }, [account, open]);

  const connect = useMutation({
    mutationFn: (values: MailConnectionInput) =>
      dataProvider.mailAccountConnect(values),
    onSuccess: (connectedAccount) => {
      queryClient.setQueryData(MAIL_ACCOUNT_QUERY_KEY, connectedAccount);
      setOpen(false);
      notify("crm.mail.account.connected", {
        messageArgs: { _: "Почта подключена" },
      });
    },
    onError: (error: Error) =>
      notify(error.message, { type: "error", autoHideDuration: 8000 }),
  });

  const disconnect = useMutation({
    mutationFn: () => dataProvider.mailAccountDisconnect(),
    onSuccess: () => {
      queryClient.setQueryData(MAIL_ACCOUNT_QUERY_KEY, null);
      notify("crm.mail.account.disconnected", {
        messageArgs: { _: "Почтовый ящик отключён" },
      });
    },
    onError: (error: Error) => notify(error.message, { type: "error" }),
  });

  const sync = useMutation({
    mutationFn: () => dataProvider.mailSync(),
    onSuccess: ({ imported }) => {
      queryClient.invalidateQueries({ queryKey: MAIL_ACCOUNT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["email_messages"] });
      notify("crm.mail.sync.complete", {
        messageArgs: {
          _: `Синхронизация завершена: ${imported} новых`,
          count: imported,
        },
      });
    },
    onError: (error: Error) =>
      notify(error.message, { type: "error", autoHideDuration: 8000 }),
  });

  const setValue = <K extends keyof ConnectionForm>(
    field: K,
    value: ConnectionForm[K],
  ) => setForm((current) => ({ ...current, [field]: value }));

  const handleEmailBlur = () => {
    const preset = getPreset(form.email);
    if (!preset || form.imapHost || form.smtpHost) return;
    setForm((current) => ({
      ...current,
      ...preset,
      imapUsername: current.imapUsername || current.email,
      smtpUsername: current.smtpUsername || current.email,
    }));
  };

  const applyMailcowPreset = () => {
    setForm((current) => ({
      ...current,
      imapHost: "mail.serhey.ru",
      imapPort: 8993,
      imapSecure: true,
      imapUsername: current.imapUsername || current.email,
      smtpHost: "mail.serhey.ru",
      smtpPort: 8465,
      smtpSecure: true,
      smtpUsername: current.smtpUsername || current.email,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    connect.mutate({
      ...form,
      imapUsername: form.imapUsername || form.email,
      smtpUsername: form.smtpUsername || form.email,
      smtpPassword: form.smtpPassword || form.imapPassword,
    });
  };

  if (isPending) {
    return <div className="text-sm text-muted-foreground">Загрузка почты…</div>;
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      {!account ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {translate("crm.mail.account.description", {
              _: "Подключите почтовый ящик по IMAP и SMTP, чтобы получать и отправлять письма из CRM.",
            })}
          </p>
          <Button type="button" variant="outline" onClick={() => setOpen(true)}>
            <Settings2 className="size-4" />
            {translate("crm.mail.account.connect", { _: "Подключить почту" })}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <MailCheck className="size-4 text-muted-foreground" />
            <span className="font-medium">{account.email}</span>
            <Badge
              variant={account.status === "error" ? "destructive" : "secondary"}
            >
              {account.status === "error" ? "Ошибка" : "Подключено"}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground">
            IMAP: {account.imap_host}:{account.imap_port} · SMTP:{" "}
            {account.smtp_host}:{account.smtp_port}
          </div>
          {account.last_synced_at && (
            <div className="text-xs text-muted-foreground">
              Последняя синхронизация:{" "}
              {new Date(account.last_synced_at).toLocaleString()}
            </div>
          )}
          {account.last_error && (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Ошибка почты</AlertTitle>
              <AlertDescription>{account.last_error}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
            >
              <RefreshCw
                className={sync.isPending ? "size-4 animate-spin" : "size-4"}
              />
              Синхронизировать
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(true)}
            >
              Изменить
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              Отключить
            </Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <DialogHeader>
              <DialogTitle>Подключение IMAP/SMTP</DialogTitle>
              <DialogDescription>
                Для Gmail, Яндекса и Mail.ru обычно нужен отдельный пароль
                приложения. Обычный пароль аккаунта может не работать.
              </DialogDescription>
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={applyMailcowPreset}
                >
                  Заполнить настройки mail.serhey.ru
                </Button>
              </div>
            </DialogHeader>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" className="sm:col-span-1">
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(event) => setValue("email", event.target.value)}
                  onBlur={handleEmailBlur}
                  autoComplete="email"
                />
              </Field>
              <Field label="Имя отправителя">
                <Input
                  value={form.displayName}
                  onChange={(event) =>
                    setValue("displayName", event.target.value)
                  }
                  placeholder="Иван — отдел продаж"
                />
              </Field>

              <div className="sm:col-span-2 border-t pt-4 font-medium">
                Входящая почта — IMAP
              </div>
              <Field label="IMAP-сервер">
                <Input
                  required
                  value={form.imapHost}
                  onChange={(event) => setValue("imapHost", event.target.value)}
                  placeholder="imap.example.ru"
                />
              </Field>
              <Field label="IMAP-порт">
                <Input
                  type="number"
                  required
                  min={1}
                  max={65535}
                  value={form.imapPort}
                  onChange={(event) =>
                    setValue("imapPort", Number(event.target.value))
                  }
                />
              </Field>
              <Field label="Логин IMAP">
                <Input
                  value={form.imapUsername}
                  onChange={(event) =>
                    setValue("imapUsername", event.target.value)
                  }
                  placeholder={form.email || "Обычно совпадает с email"}
                  autoComplete="username"
                />
              </Field>
              <Field label="Пароль IMAP">
                <Input
                  type="password"
                  required
                  value={form.imapPassword}
                  onChange={(event) =>
                    setValue("imapPassword", event.target.value)
                  }
                  autoComplete="new-password"
                />
              </Field>
              <TlsField
                checked={form.imapSecure}
                onCheckedChange={(checked) => setValue("imapSecure", checked)}
                label="IMAP через TLS (обычно порт 993)"
              />

              <div className="sm:col-span-2 border-t pt-4 font-medium">
                Исходящая почта — SMTP
              </div>
              <Field label="SMTP-сервер">
                <Input
                  required
                  value={form.smtpHost}
                  onChange={(event) => setValue("smtpHost", event.target.value)}
                  placeholder="smtp.example.ru"
                />
              </Field>
              <Field label="SMTP-порт">
                <Input
                  type="number"
                  required
                  min={1}
                  max={65535}
                  value={form.smtpPort}
                  onChange={(event) =>
                    setValue("smtpPort", Number(event.target.value))
                  }
                />
              </Field>
              <Field label="Логин SMTP">
                <Input
                  value={form.smtpUsername}
                  onChange={(event) =>
                    setValue("smtpUsername", event.target.value)
                  }
                  placeholder={form.email || "Обычно совпадает с email"}
                  autoComplete="username"
                />
              </Field>
              <Field label="Пароль SMTP">
                <Input
                  type="password"
                  value={form.smtpPassword}
                  onChange={(event) =>
                    setValue("smtpPassword", event.target.value)
                  }
                  placeholder="Если пусто — используется пароль IMAP"
                  autoComplete="new-password"
                />
              </Field>
              <TlsField
                checked={form.smtpSecure}
                onCheckedChange={(checked) => setValue("smtpSecure", checked)}
                label="Прямой TLS (порт 465); выключите для STARTTLS (587)"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Отмена
              </Button>
              <Button type="submit" disabled={connect.isPending}>
                {connect.isPending
                  ? "Проверяем соединение…"
                  : "Проверить и подключить"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={`space-y-2 ${className || ""}`}>
    <Label>{label}</Label>
    {children}
  </div>
);

const TlsField = ({
  checked,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
}) => (
  <label className="sm:col-span-2 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
    <Checkbox
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
    />
    {label}
  </label>
);
